import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect } from "react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Pilcrow, Sparkles, Wand2 } from "lucide-react";


/**
 * Toolbar button used inside the editor toolbar. Defined at module scope
 * (not inside the parent component) so React keeps a stable component type
 * across renders.
 */
function Btn({ onClick, active, children, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`rounded p-1.5 transition-colors ${active ? "bg-aubergine text-bone" : "text-aubergine/65 hover:bg-aubergine/10 hover:text-aubergine"}`}
    >
      {children}
    </button>
  );
}

/**
 * Lightweight WYSIWYG (Tiptap) for the admin article editor.
 * @param {{
 *   value:string,
 *   onChange:(html:string)=>void,
 *   onInsertImage?:()=>Promise<string|null>,
 *   onAIOutline?:()=>void,
 *   onAIRephrase?:(selectedText:string)=>void,
 *   editorRef?:any,
 * }} props
 */
export function RichEditor({ value, onChange, onInsertImage, onAIOutline, onAIRephrase, editorRef }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Image.configure({ inline: false }),
      Placeholder.configure({ placeholder: "Write your article…" }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
    editorProps: { attributes: { class: "tiptap-editor", "data-testid": "rich-editor" } },
  });

  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = async () => {
    if (onInsertImage) {
      const url = await onInsertImage();
      if (url) editor.chain().focus().setImage({ src: url }).run();
      return;
    }
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="rounded-lg border border-aubergine/15 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-aubergine/10 px-2 py-2 bg-bone/50">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} testid="editor-bold" title="Bold"><Bold className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} testid="editor-italic" title="Italic"><Italic className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} testid="editor-strike" title="Strike"><Strikethrough className="h-3.5 w-3.5" /></Btn>
        <span className="mx-1 h-4 w-px bg-aubergine/15" />
        <Btn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} testid="editor-paragraph" title="Paragraph"><Pilcrow className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid="editor-h2" title="Heading 2"><Heading2 className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} testid="editor-h3" title="Heading 3"><Heading3 className="h-3.5 w-3.5" /></Btn>
        <span className="mx-1 h-4 w-px bg-aubergine/15" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} testid="editor-ul" title="Bullet list"><List className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} testid="editor-ol" title="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} testid="editor-quote" title="Quote"><Quote className="h-3.5 w-3.5" /></Btn>
        <span className="mx-1 h-4 w-px bg-aubergine/15" />
        <Btn onClick={setLink} active={editor.isActive("link")} testid="editor-link" title="Link"><LinkIcon className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={insertImage} testid="editor-image" title="Image"><ImageIcon className="h-3.5 w-3.5" /></Btn>
        <span className="mx-1 h-4 w-px bg-aubergine/15" />
        <Btn onClick={() => editor.chain().focus().undo().run()} testid="editor-undo" title="Undo"><Undo className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} testid="editor-redo" title="Redo"><Redo className="h-3.5 w-3.5" /></Btn>
        {(onAIOutline || onAIRephrase) && <span className="mx-1 h-4 w-px bg-aubergine/15" />}
        {onAIOutline && (
          <button
            type="button"
            onClick={onAIOutline}
            data-testid="editor-ai-outline"
            title="AI outline"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-champagne/40 bg-champagne/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-champagne hover:bg-champagne/20 transition-colors"
          >
            <Sparkles className="h-3 w-3" /> AI outline
          </button>
        )}
        {onAIRephrase && (
          <button
            type="button"
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to, " ");
              onAIRephrase(selectedText);
            }}
            data-testid="editor-ai-rephrase"
            title="AI rephrase selection"
            className="inline-flex items-center gap-1.5 rounded-md border border-champagne/40 bg-champagne/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-champagne hover:bg-champagne/20 transition-colors"
          >
            <Wand2 className="h-3 w-3" /> AI rephrase
          </button>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
